import { fabric } from 'fabric'
import { v4 as guid } from 'uuid'
import { debounce, isEmpty, pick } from 'lodash-es'
import WbDesignOption from './designOption'
import type MyArticle from '@/models/myArticle'
import appConfig from '@/services/appConfig'
import utils from '@/services/utils'
import { useUserStore } from '@/store/userData'
import { whiteboardConstants } from '@/models/constants'
import { getDesignOptionAssets } from '@/api/dune/asset'
import type DuneAsset from '@/models/duneAsset'

interface IWbModelDetailsOptions extends fabric.ITextboxOptions, IWbObjectProps {
  id?: string
  catalogCode?: number
  articleId?: number
  modelId?: number
  article?: MyArticle
  showLabels?: boolean
  lock?: boolean
  attributes?: string[]
  attributesSeparator?: string
  attributePlaceHolder?: string
  preventUnlock?: boolean
  localOnly?: boolean
  preventDelete?: boolean
  excludeFromGroupSelection?: boolean
  showDesignOptions?: boolean
  designOptionData?: IDesignOptionPosition[]
  modelNumber?: string
  allowDesignOption?: boolean
}

// eslint-disable-next-line ts/ban-ts-comment
// @ts-expect-error
export default class WbModelDetails extends fabric.Textbox implements IWbObject {
  public id: string
  public catalogCode?: number
  public modelId?: number
  public articleId?: number
  public article?: MyArticle
  public showLabels: boolean
  public attributes?: string[]
  public attributesSeparator?: string
  public attributePlaceHolder?: string
  public localOnly?: boolean
  public type = whiteboardConstants.objectTypes.modelDetails
  public lock: boolean
  public preventUnlock: boolean
  public connectable: boolean
  public preventDelete?: boolean
  public excludeFromGroupSelection?: boolean
  public canBeLinkedTo = true
  public canLink = false
  public link = undefined
  public designOptions: DuneAsset[]
  public showDesignOptions: boolean
  public designOptionData: IDesignOptionPosition[] = []
  private designOptionsObjects: fabric.Object[] = []
  public highlighted = false
  public _isMoving = false
  public modelNumber: string
  public allowDesignOption = true
  public editableProps: Record<string, IWbObjectProp> = {
    textColor: { name: 'Text Color', type: 'textColor', editable: true },
    font: { name: 'Font', type: 'font', editable: true },
    fontSize: { name: 'Font Size', type: 'fontSize', editable: true },
    textAlign: { name: 'Align', type: 'textAlign', editable: true },
    fontStyle: { name: 'Font Style', type: 'fontStyle', editable: true },
    lock: { name: 'Lock', type: 'lock', editable: true, editableWhenLocked: true },
    showLabels: { name: 'Show Labels', type: 'showLabels', editable: true },
    showDesignOptions: { name: 'Show Design Options', type: 'showDesignOptions', editable: true, editableWhenLocked: true },
    attributes: { name: 'Add Attributes', type: 'attributes', editable: true },
    addDiscussion: { name: 'Add Comment', type: 'addDiscussion', editable: true, editableWhenLocked: true },
    info: { name: 'Info', type: 'info', editable: true, editableWhenLocked: true },
  }

  public actions: Record<string, IWObjectActions> = {
    selectSimilar: { action: 'selectSimilar', label: 'Select Similar', faicon: 'fa-light fa-check-double', showInSubMenu: true },
    bringFront: { action: 'bringFront', label: 'Bring to Front', faicon: 'fa-light fa-bring-front', showInSubMenu: true },
    sendBack: { action: 'sendBack', label: 'Send to Back', faicon: 'fa-light fa-send-back', showInSubMenu: true },
    // group: { action: 'group', label: 'Group', faicon: 'fa-light fa-object-group', showInSubMenu: true, multiple: true },
    cloneAsImage: { action: 'cloneAsImage', label: 'Copy as Image', faicon: 'fa-light fa-clone', showInSubMenu: true },
    delete: { action: 'delete', label: 'Remove', faicon: 'fa-light fa-trash-can', showInSubMenu: true },
  }

  public static defaultAttributes = ['ModelNumber', 'ModelName']

  constructor(text: string, options: IWbModelDetailsOptions) {
    super(text, options)
    const userStore = useUserStore()
    const userWhiteboardSettings = utils.isDefined(userStore.settingsContainer) && userStore.settingsContainer.whiteboard ? userStore.settingsContainer.whiteboard : ''
    this.id = options?.id || guid()
    this.catalogCode = options?.catalogCode
    this.modelId = options?.modelId
    this.articleId = options?.articleId
    this.article = options?.article
    this.showLabels = utils.isDefined(options) && utils.isDefined(options.showLabels) ? options.showLabels : true
    this.attributes = options?.attributes?.length ? options.attributes : [...WbModelDetails.defaultAttributes]
    this.attributesSeparator = options?.attributesSeparator || '\n'
    this.attributePlaceHolder = options?.attributePlaceHolder || '[Blank]'
    this.fontFamily = options?.fontFamily
      ? options?.fontFamily
      : (userWhiteboardSettings !== '' && userWhiteboardSettings.modelDetailsFont && userWhiteboardSettings.modelDetailsFont !== '')
          ? userWhiteboardSettings.modelDetailsFont
          : utils.isDefined(appConfig.dynamicConfig) && utils.isDefined(appConfig.dynamicConfig.whiteboardDefaultFont) && appConfig.dynamicConfig.whiteboardDefaultFont.length
            ? appConfig.dynamicConfig.whiteboardDefaultFont
            : 'Helvetica'
    this.fontSize = options?.fontSize || 14
    this.textAlign = options?.textAlign || 'center'
    this.lock = options?.lock || false
    this.editable = false
    this.connectable = true
    this.localOnly = options?.localOnly || false
    this.allowDesignOption = utils.isDefined(options?.allowDesignOption) ? options?.allowDesignOption : true

    this.preventUnlock = options?.preventUnlock || false
    this.preventDelete = options?.preventDelete || false
    this.excludeFromGroupSelection = options?.excludeFromGroupSelection || false
    this.designOptions = []
    this.showDesignOptions = utils.isDefined(options?.showDesignOptions) ? Boolean(options?.showDesignOptions) : userStore.activeCatalog && userStore.userPreferences && userStore.userPreferences.whiteboard && userStore.userPreferences.whiteboard.settings && userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode] && userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode].showModelDesignOptions ? userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode].showModelDesignOptions : false
    this.designOptionsObjects = []
    this.designOptionData = options?.designOptionData || []
    this.modelNumber = options?.modelNumber || ''

    // const userStore = useUserStore()
    // const catalogDetails = this.catalogCode ? (userStore.linkedCatalogDetails[this.catalogCode] || userStore.activeCatalog) : userStore.activeCatalog!
    // // if previous season model article need to add the current season article as well in the live query to support the updates in sku count of current catalog.
    // const queryCriterion: Array<[number, number, string]> = [[userStore.activeCatalog!.CatalogCode, 1, this.modelNumber || '']]
    // if (catalogDetails.CatalogCode !== userStore.activeCatalog?.CatalogCode) {
    //   queryCriterion.push([+catalogDetails!.CatalogCode, 1, this.modelNumber || ''])
    // }
    // const observable = liveQuery(async () => await appConfig.DB!.articles.where('[CatalogCode+Status+ModelNumber]').anyOf(queryCriterion).toArray())
    // const subscription = observable.subscribe(async (response) => {
    //   // As discussed with Andre we dont need to set article again when we first time create the object as already we are loading the article
    //   if (!this.isLoadingFirstTime) {
    //     const article = response.find(article => article.CatalogCode === catalogDetails.CatalogCode)
    //     if (article) {
    //       const articles = await appConfig.DB!.buildMyArticles([article], catalogDetails, utils.getIndexedLinkedCatalogPriceGroupByLowercaseName(userStore.linkedCatalogDetails), userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, true)
    //       this.article = articles[0]
    //       this.set('text', await WbModelDetails.getText(this.article, this.showLabels, this.attributes, this.attributesSeparator, this.attributePlaceHolder))
    //       this.dirty = true
    //       this.canvas?.requestRenderAll()
    //       this.canvas?.fire('object:modified', { target: this, ignoreHistory: true })
    //     }
    //   }
    //   else {
    //     this.isLoadingFirstTime = false
    //   }
    // })
    // this.subscriptions.push(subscription)

    this.set('text', text) // super(text, options) is not setting the text property with the text value rather it display the static value received from server
    this.setLock(this.lock)

    this.on('added', () => {
      this.handleDesignOptionsVisibility(this.showDesignOptions)
    })

    this.on('selected', () => {
      this.selected = true
    })

    this.on('deselected', () => {
      this.selected = false
    })

    this.on('removed', () => {
      if (this.designOptionsObjects.length > 0) {
        this.canvas?.remove(...this.designOptionsObjects)
      }
    })
    this.on('moving', () => {
      this._isMoving = true
    })

    this.on('mouseup', () => {
      this._isMoving = false
    })

    this.on('scaling', () => {
      this.updateLinkWhenUpdated()
      this.canvas?.fire('object:modified', { target: this })

      this.canvas?.requestRenderAll()
    })
    this.stateProperties = this.stateProperties?.concat(['showLabels', 'lock', 'attributes', 'preventUnlock'])
  }

  updateLinkWhenUpdated() {
    const articleCoords = fabric.util.qrDecompose(this.calcTransformMatrix())

    this.designOptionsObjects.forEach((designObject) => {
      const designOptionObject = designObject as WbDesignOption
      const assetId = designOptionObject.assetId
      if (assetId) {
        const pos = this.designOptionData.find(p => p.id === assetId)
        if (!pos) { return }
        const designObjectCoords = fabric.util.qrDecompose(designOptionObject.calcTransformMatrix())
        // Convert from center-origin to top-left
        const topLeftX = designObjectCoords.translateX - (designOptionObject.width! * designOptionObject.scaleX!) / 2
        const topLeftY = designObjectCoords.translateY - (designOptionObject.height! * designOptionObject.scaleY!) / 2

        pos.link = {
          x: (topLeftX - articleCoords.translateX) / this.scaleX!,
          y: (topLeftY - articleCoords.translateY) / this.scaleY!,
        }
      }
    })
  }

  onServerUpdate() {
    if (this._isMoving) {
      this.updateLinkWhenUpdated()
    }
    this.canvas?.requestRenderAll()
  }

  private async createDesignOptionObjects(designOptions: DuneAsset[]) {
    this.canvas?.remove(...this.designOptionsObjects)
    this.designOptionsObjects = []

    const horizontalGap = 20 // first time
    const verticalStartGap = 50 // first time
    const gapBetweenDesignOptions = 10 // first time
    const parentMatrix = this.calcTransformMatrix()
    const parentCoords = fabric.util.qrDecompose(parentMatrix)
    const invParentMatrix = fabric.util.invertTransform(parentMatrix)
    const scaledW = (this.width || 0) * (this.scaleX || 1)
    const scaledH = (this.height || 0) * (this.scaleY || 1)
    const baseLeft = parentCoords.translateX + scaledW / 2 + horizontalGap
    let currentTop = parentCoords.translateY - scaledH / 2 + verticalStartGap
    const existingDesignOptions: DuneAsset[] = []
    const nonExistingDesignOptions: DuneAsset[] = []
    for (const designOption of designOptions) {
      const position = this.designOptionData.find(p => p.id === designOption.Id)
      if (position) {
        existingDesignOptions.push(designOption)
      }
      else {
        nonExistingDesignOptions.push(designOption)
      }
    }
    let totalExistingHeight = 0

    // --- existing options ---
    for (const designOption of existingDesignOptions) {
      const position = this.designOptionData.find(p => p.id === designOption.Id)!
      const currentHeightAndWidth = WbDesignOption.getDesignOptionHeightWidth(designOption.Width, designOption.Height, position.scale)

      const maxActualSize = Math.max(designOption.Width, designOption.Height)
      const aspectRatio = designOption.Width / designOption.Height
      const currentMaxSize = Math.max(currentHeightAndWidth.scaledWidth, currentHeightAndWidth.scaledHeight)
      const newResolution = WbDesignOption.getNearestResolution(currentMaxSize, maxActualSize)

      const width = designOption.Height > designOption.Width
        ? Math.round(newResolution / (currentHeightAndWidth.scaledHeight / currentHeightAndWidth.scaledWidth))
        : newResolution
      const height = designOption.Height > designOption.Width
        ? newResolution
        : Math.round(newResolution / aspectRatio)

      const scaleX = currentHeightAndWidth.scaledWidth / width
      const scaleY = currentHeightAndWidth.scaledHeight / height

      // 🔑 compute world position from stored local link
      const worldPt = fabric.util.transformPoint(
        new fabric.Point(position.link.x, position.link.y),
        parentMatrix,
      )

      const options = { article: this.article, assetId: designOption.Id, asset: designOption, articleId: this.articleId, assetStorageFile: designOption.StorageFile, top: worldPt.y, left: worldPt.x, scaleX, scaleY }
      const designOptionObject = await WbDesignOption.loadFromUrl(options, width, height) as WbDesignOption
      totalExistingHeight += currentHeightAndWidth.scaledHeight + gapBetweenDesignOptions
      this.setDesignOptionEventHandlers(designOptionObject, designOption.Id)
      // recompute link back into local coords
      const childCoords = fabric.util.qrDecompose(designOptionObject.calcTransformMatrix())
      const childWorld = new fabric.Point(childCoords.translateX, childCoords.translateY)
      const childLocal = fabric.util.transformPoint(childWorld, invParentMatrix)
      designOptionObject.link = { id: this.id, x: childLocal.x, y: childLocal.y } as any
      this.designOptionsObjects.push(designOptionObject)
      this.canvas?.add(designOptionObject)
    }

    currentTop += totalExistingHeight
    // --- new options ---
    for (const designOption of nonExistingDesignOptions) {
      // convert new world coords → local
      const localPt = fabric.util.transformPoint(
        new fabric.Point(baseLeft, currentTop),
        invParentMatrix,
      )

      const position = {
        id: designOption.Id,
        link: { x: localPt.x, y: localPt.y },
        scale: WbDesignOption.getDesignOptionScale(designOption.Width, designOption.Height),
      }
      this.designOptionData.push(position)
      //  convert back local → world for placement
      const worldPt = fabric.util.transformPoint(
        new fabric.Point(localPt.x, localPt.y),
        parentMatrix,
      )

      const currentData = WbDesignOption.getDesignOptionHeightWidth(designOption.Width, designOption.Height, position.scale)
      const options = { article: this.article, assetId: designOption.Id, asset: designOption, articleId: this.articleId, assetStorageFile: designOption.StorageFile, top: worldPt.y, left: worldPt.x }
      const designOptionObject = await WbDesignOption.loadFromUrl(options, currentData.scaledWidth, currentData.scaledHeight) as WbDesignOption
      this.setDesignOptionEventHandlers(designOptionObject, designOption.Id)

      //  store link back in local coords
      const childCoords = fabric.util.qrDecompose(designOptionObject.calcTransformMatrix())
      const childWorld = new fabric.Point(childCoords.translateX, childCoords.translateY)
      const childLocal = fabric.util.transformPoint(childWorld, invParentMatrix)

      designOptionObject.link = { id: this.id, x: childLocal.x, y: childLocal.y } as any

      this.designOptionsObjects.push(designOptionObject)
      this.canvas?.add(designOptionObject)
      currentTop += designOptionObject.height! + gapBetweenDesignOptions
    }
    if (nonExistingDesignOptions.length > 0) {
      this.canvas?.fire('object:modified', { target: this })
    }
    this.canvas?.requestRenderAll()
  }

  private handleDesignOptionsVisibility(visible: boolean) {
    const userStore = useUserStore()
    if (this.allowDesignOption) {
      if (visible) {
        const catalogDetails = userStore.activeCatalog
        if (catalogDetails && this.article) {
          getDesignOptionAssets(catalogDetails.DuneContext, catalogDetails.ContextKey, this.article.ModelNumber, false, whiteboardConstants.abortController.signal)
            .then((assets) => {
              this.designOptions = assets
              if (this.designOptions.length > 0) {
                this.createDesignOptionObjects(this.designOptions)
                // remove deleted design options data
                const validIds = new Set(this.designOptions.map(r => r.Id))
                this.designOptionData = this.designOptionData.filter(r => validIds.has(r.id))
              }
              else if (this.showDesignOptions) { // if no design options then set the show design options false
                this.designOptionData = []
                // this.setProp('showDesignOptions', { showDesignOptions: false }, true)
              }
            }).catch((_e) => {
              if (this.designOptions.length > 0) {
                this.createDesignOptionObjects(this.designOptions)
                // remove deleted design options data
                const validIds = new Set(this.designOptions.map(r => r.Id))
                this.designOptionData = this.designOptionData.filter(r => validIds.has(r.id))
              }
              else if (this.showDesignOptions) { // if no design options then set the show design options false
                this.designOptionData = []
                // this.setProp('showDesignOptions', { showDesignOptions: false }, true)
              }
            })
        }
      }
      else {
        this.designOptionsObjects.forEach((obj) => {
          if (this.canvas?.contains(obj)) {
            this.canvas?.remove(obj)
          }
        })
        this.designOptionsObjects = []
        this.canvas?.requestRenderAll()
      }
    }
  }

  private setDesignOptionEventHandlers(designOptionObject: fabric.Object, assetId: string) {
    const designObject = designOptionObject as WbDesignOption
    designOptionObject.on('modified', () => {
      const articleCoords = fabric.util.qrDecompose(this.calcTransformMatrix())
      const pos = this.designOptionData.find(p => p.id === assetId)
      if (!pos) { return }

      // pos.scale = pos.scale || 1

      const resourceCoords = fabric.util.qrDecompose(designOptionObject.calcTransformMatrix())

      pos.link = {
        // id: this.id,
        x: (resourceCoords.translateX - articleCoords.translateX) / this.scaleX!,
        y: (resourceCoords.translateY - articleCoords.translateY) / this.scaleY!,
      }
      this.canvas?.fire('object:modified', { target: this })
    })
    designObject.on('scaling', () => {
      const debouncedCheckAndReplace = debounce(() => {
        if (designObject.isUpdatingImage) { return }
        this.applyScaling(designOptionObject)
      }, 2000)
      if (!designObject.isUpdatingImage) {
        debouncedCheckAndReplace()
      }
    })
  }

  applyScaling(designOptionObject) {
    const userStore = useUserStore()
    const pos = this.designOptionData.find(p => p.id === designOptionObject.asset.Id)
    if (!pos) { return }
    const currentWidth = designOptionObject.width * designOptionObject.scaleX
    const currentHeight = designOptionObject.height * designOptionObject.scaleY
    const currentMaxSize = Math.max(currentWidth, currentHeight)
    const maxActualSize = Math.max(designOptionObject.asset.Width, designOptionObject.asset.Height)
    const aspectRatio = designOptionObject.asset.Width / designOptionObject.asset.Height

    const newResolution = WbDesignOption.getNearestResolution(currentMaxSize, maxActualSize)
    if (newResolution > Math.max(designOptionObject.width, designOptionObject.height)) {
      let newImageWidth = currentWidth
      let newImageHeight = Math.round(newResolution / aspectRatio)
      if (currentHeight > currentWidth) {
        newImageHeight = Math.round(currentHeight)
        newImageWidth = Math.round(newResolution / (currentHeight / currentWidth))
      }
      designOptionObject.isUpdatingImage = true
      const width = designOptionObject.asset.Height > designOptionObject.asset.Width ? newImageWidth : newResolution
      const height = designOptionObject.asset.Height > designOptionObject.asset.Width ? newResolution : newImageHeight
      const url = `${appConfig.AssetsUrl}/assets/content/${designOptionObject.assetStorageFile}?ContextKey=${encodeURIComponent(userStore.activeCatalog!.ContextKey)}&f=webp&w=${width}&h=${height}&trim=true`
      fabric.util.loadImage(url, (newImgEl) => {
        designOptionObject.setElement(newImgEl)
        designOptionObject.set({
          width,
          height,
          scaleX: width / currentWidth,
          scaleY: height / currentHeight,
        } as unknown as Partial<this>)
        pos.scale = (currentMaxSize / maxActualSize) || pos.scale
        designOptionObject.isUpdatingImage = false
        designOptionObject.canvas?.requestRenderAll()
        designOptionObject.canvas?.fire('object:modified', { target: this })
      }, null, 'anonymous')
    }
  }

  async setProp(prop: string, value: any, ignoreHistory: boolean = false, notifyObjectModified: boolean = true) {
    switch (prop) {
      case 'top':
        this.set('top', value.top)
        break
      case 'left':
        this.set('left', value.left)
        break
      case 'textColor':
        this.set('fill', value.textColor)
        break
      case 'font':
        this.set('fontFamily', value.font)
        break
      case 'fontSize':
        this.set('fontSize', value.fontSize)
        break
      case 'textAlign':
        this.set('textAlign', value.textAlign)
        break
      case 'bold':
        this.set('fontWeight', value.bold ? 'bold' : '')
        break
      case 'italic':
        this.set('fontStyle', value.italic ? 'italic' : '')
        break
      case 'underline':
        this.set('underline', value.underline)
        break
      case 'lock':
        this.setLock(value.lock)
        break
      case 'showLabels':
        this.set('showLabels', value.showLabels)
        this.set('text', await this.getAttributesValue(this.attributes, value.showLabels, this.attributesSeparator, this.attributePlaceHolder))
        break
      case 'attributes':
        this.set('attributes', value.attributes)
        this.set('text', await this.getAttributesValue(value.attributes, this.showLabels, this.attributesSeparator, this.attributePlaceHolder))
        break
      case 'text':
        // value in case of set text value should be fetch from article
        this.set('text', await this.getAttributesValue(this.attributes, this.showLabels, this.attributesSeparator, this.attributePlaceHolder))
        break
      case 'height':
        this.set('height', value)
        break
      case 'showDesignOptions':
        this.set('showDesignOptions', value.showDesignOptions)
        this.handleDesignOptionsVisibility(Boolean(value.showDesignOptions))
        break
      default:
        console.warn('Attempting to set unsupported WbObjectProp', prop, value)
        return
    }
    this.dirty = true
    this.canvas?.requestRenderAll()
    if (notifyObjectModified) {
      this.canvas?.fire('object:modified', { target: this, ignoreHistory })
    }
  }

  getProp(prop: string) {
    const result: any = {}
    switch (prop) {
      case 'textColor':
        result.textColor = this.fill
        break
      case 'font':
        result.font = this.fontFamily
        break
      case 'fontSize':
        result.fontSize = this.fontSize
        break
      case 'textAlign':
        result.textAlign = this.textAlign
        break
      case 'bold':
        result.bold = !!this.fontWeight
        break
      case 'italic':
        result.italic = !!this.fontStyle
        break
      case 'underline':
        result.underline = !!this.underline
        break
      case 'lock':
        result.lock = this.lock
        break
      case 'showLabels':
        result.showLabels = this.showLabels
        break
      case 'attributes':
        result.attributes = this.attributes
        break
      case 'text':
        result.text = this.text
        break
      case 'showDesignOptions':
        result.showDesignOptions = this.showDesignOptions
        break
      default:
        console.warn('Attempting to get unsupported WbObjectProp', prop)
    }
    return result
  }

  setLock(lock: boolean) {
    this.set('lock', lock)
    this.set('lockMovementX', lock)
    this.set('lockMovementY', lock)
    this.set('lockRotation', lock)
    this.set('lockScalingFlip', lock)
    this.set('lockScalingX', lock)
    this.set('lockScalingY', lock)
    this.set('hasControls', !lock)
  }

  override toObject() {
    const propsToPluck = [
      'id',
      'type',
      'top',
      'left',
      'angle',
      'width',
      'height',
      'scaleX',
      'scaleY',
      'fill',
      'backgroundColor',
      'fontFamily',
      'fontSize',
      'fontStyle',
      'underline',
      'fontWeight',
      'textAlign',
      'lock',
      'showLabels',
      'modelId',
      'modelNumber',
      'articleId',
      'catalogCode',
      'attributes',
      'attributesSeparator',
      'attributePlaceHolder',
      'preventUnlock',
      'localOnly',
      'showDesignOptions',
      'designOptionData',
      'allowDesignOption',
    ]
    const returnObject = pick(this, propsToPluck)
    return returnObject
  }

  static fromObject(object: WbModelDetails, callback?: Function) {
    if ((object.modelId != null || (object.modelNumber !== null)) && object.catalogCode != null) {
      this.loadModelDetailsByNumber(object.catalogCode, object)
        .then((v) => { callback && callback(v) })
    }
    else {
      console.log('Should never happen every object either should have modelId or model number', object)
    }
  }

  // this function will always resolve to a value
  static async getText(article: MyArticle, showLabels: boolean, attributes?: string[], attributesSeparator?: string, attributePlaceHolder?: string) {
    const userStore = useUserStore()
    const attributeData: any[] = []
    if (attributes && attributes.length) {
      for (let i = 0; i < attributes.length; i++) {
        const attributeSystemName = attributes[i]
        let attributeValue
        if (utils.isDefined(userStore.myAttributes) && utils.isDefined(userStore.myAttributes[attributeSystemName]) && userStore.myAttributes[attributeSystemName].IsModelLevel) {
          attributeValue = await utils.getAttributeTypeSpecificValue(userStore.myAttributes![attributeSystemName], article, appConfig.DB, userStore.activeCatalog, userStore.priceGroups)
          if (!utils.isValidStringValue(attributeValue)) {
            attributeValue = attributePlaceHolder
          }
          let label = ''
          const valEmpty = isEmpty(attributeValue)
          if ((showLabels || valEmpty || attributeValue === attributePlaceHolder) && userStore.activeCatalog) {
            const whiteboardLabelAttributeConfigValue = userStore.activeCatalog.Config.WhiteboardLabelAttributes
            if (whiteboardLabelAttributeConfigValue === undefined
              || (utils.isDefined(whiteboardLabelAttributeConfigValue) && Array.isArray(whiteboardLabelAttributeConfigValue) && whiteboardLabelAttributeConfigValue!.includes(userStore.myAttributes[attributeSystemName].SystemName))) {
              label = userStore.myAttributes[attributeSystemName].DisplayName
            }
          }
          attributeData.push(showLabels && label !== '' ? `${label}: ${attributeValue}` : (valEmpty || attributeValue === attributePlaceHolder) && label !== '' ? `[${label}]` : attributeValue)
        }
        else if (userStore.activeCatalog!.CatalogCode.toString() === attributeSystemName || userStore.linkedCatalogDetails.hasOwnProperty(attributeSystemName)) { // attributeSystemName for SKU is set as catalog code
          const currentCatalog = userStore.activeCatalog!.CatalogCode.toString() === attributeSystemName ? userStore.activeCatalog : userStore.linkedCatalogDetails[attributeSystemName]
          try {
            attributeValue = article.hasOwnProperty(attributeSystemName) ? article[attributeSystemName] : await appConfig.DB!.getModelSKUCount(article.ModelNumber, currentCatalog.CatalogCode, whiteboardConstants.abortController.signal)
          }
          catch (error) {
            console.error('Unable get model SKU count \n', error)
            attributeValue = attributePlaceHolder
          }
          article[attributeSystemName] = attributeValue
          attributeData.push(`${currentCatalog.Season} SKU: ${attributeValue}`)
        }
        else if (attributeSystemName === 'currentSKuCount' && userStore.activeCatalog) {
          try {
            attributeValue = article.hasOwnProperty(attributeSystemName) ? article[attributeSystemName] : await appConfig.DB!.getModelSKUCount(article.ModelNumber, userStore.activeCatalog.CatalogCode, whiteboardConstants.abortController.signal)
          }
          catch (error) {
            console.error('Unable get model SKU count \n', error)
            attributeValue = attributePlaceHolder
          }
          article[attributeSystemName] = attributeValue
          attributeData.push(`SKU Count: ${attributeValue}`)
        }
      }
    }
    return attributeData.join(attributesSeparator)
  }

  static async loadModelDetails(article: MyArticle, options?: IWbModelDetailsOptions) {
    const currentOptions = options || { modelId: article.ModelId, articleId: article.Id }
    currentOptions.catalogCode = article.CatalogCode
    currentOptions.modelId = article.ModelId
    currentOptions.modelNumber = article.ModelNumber
    currentOptions.articleId = article.Id
    currentOptions.showLabels = utils.isDefined(options) && utils.isDefined(options.showLabels) ? options.showLabels : true
    currentOptions.attributes = options?.attributes?.length ? options.attributes : [...WbModelDetails.defaultAttributes]
    currentOptions.attributesSeparator = options?.attributesSeparator || '\n'
    currentOptions.attributePlaceHolder = options?.attributePlaceHolder || ''
    // setting SKU to avoid api call
    if (article.Status === 0) {
      article.currentSKuCount = 0
      article[article.CatalogCode] = 0
    }
    const modelDetailsObject = new WbModelDetails(await this.getText(article, currentOptions.showLabels, currentOptions.attributes, currentOptions.attributesSeparator, currentOptions.attributePlaceHolder), currentOptions)
    modelDetailsObject.article = article
    return modelDetailsObject
  }

  static async loadModelDetailsByNumber(catalogCode: number, options?: IWbModelDetailsOptions) {
    if (options?.article) {
      return await this.loadModelDetails(options.article, options)
    }
    else if (options?.modelId) {
      const userStore = useUserStore()
      const catalogDetails = userStore.linkedCatalogDetails[catalogCode] || userStore.activeCatalog
      if (appConfig.DB && userStore.myAttributes) {
        const articles = await appConfig.DB.getFirstActiveMyArticleByModelIds([options?.modelId], catalogDetails, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, [], true, whiteboardConstants.abortController.signal)
        if (articles && articles.length) {
          return await this.loadModelDetails(articles[0], options)
        }
        else {
          console.warn('unable to find the article to refer', options)
        }
      }
    }
    else if (options?.modelNumber) {
      const userStore = useUserStore()
      const catalogDetails = userStore.linkedCatalogDetails[catalogCode] || userStore.activeCatalog
      if (appConfig.DB && userStore.myAttributes) {
        const articles = await appConfig.DB.getFirstActiveMyArticleByModelNumbers([options?.modelNumber], catalogDetails, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, [], true, whiteboardConstants.abortController.signal)
        if (articles && articles.length) {
          return await this.loadModelDetails(articles[0], options)
        }
        else {
          console.warn('unable to find the article to refer', options)
        }
      }
    }
    console.warn('Failed to load model details by modelNumber')
    return null
  }

  private async getAttributesValue(attributes: string[] | undefined, showLabels: boolean, attributesSeparator: string = '\n', attributePlaceHolder: string = '[Blank]') {
    return this.article ? await WbModelDetails.getText(this.article, showLabels, attributes, attributesSeparator, attributePlaceHolder) : ''
  }

  // override _render(ctx: CanvasRenderingContext2D): void {
  //   super._render(ctx)
  //   if (this.selected && this.article) {
  //     ctx.font = `${this.fontSize}` + 'px Helvetica'
  //     // let seasonData = userStore.activeCatalog?.CatalogCode === this.article.CatalogCode
  //     // ? userStore.activeCatalog.Season
  //     // : userStore.linkedCatalogDetails[this.article.CatalogCode].Season
  //     // const seasonDataMeasure = ctx.measureText(seasonData)
  //     ctx.fillStyle = '#1484f8'
  //     // ctx.fillRect(this.width!/2 - seasonDataMeasure.width, -this.height!/2 , seasonDataMeasure.width, 15)
  //     ctx.fillStyle = '#FFFFFF'
  //     // ctx.fillText(seasonData, this.width!/2 - seasonDataMeasure.width, -this.height! / 2 + 12)
  //   }
  // }
}

const f: any = fabric
f.WbModelDetails = WbModelDetails
